"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.composePatch = exports.composeSnapshot = exports.platformSnapshotFields = exports.extractDeepMergeFields = exports.isDeepMergeField = exports.deepMerge = exports.extractClientShape = exports.isServerField = exports.server = void 0;
const zod_1 = require("zod");
const id_1 = require("./id");
// ── server() field marker ──
const SERVER_FIELD = Symbol.for('omniflow.server_field');
// ── deepMerge() field marker ──
const DEEP_MERGE_FIELD = Symbol.for('omniflow.deep_merge_field');
const server = (schema) => {
    Object.defineProperty(schema, SERVER_FIELD, { value: true, enumerable: false });
    return schema;
};
exports.server = server;
const isServerField = (schema) => SERVER_FIELD in schema && schema[SERVER_FIELD] === true;
exports.isServerField = isServerField;
/**
 * Из domain shape оставляет только клиентские поля (без server()-маркера).
 */
const extractClientShape = (shape) => {
    const result = {};
    for (const [key, schema] of Object.entries(shape)) {
        if (!(0, exports.isServerField)(schema)) {
            result[key] = schema;
        }
    }
    return result;
};
exports.extractClientShape = extractClientShape;
const deepMerge = (schema) => {
    Object.defineProperty(schema, DEEP_MERGE_FIELD, { value: true, enumerable: false });
    return schema;
};
exports.deepMerge = deepMerge;
const isDeepMergeField = (schema) => DEEP_MERGE_FIELD in schema && schema[DEEP_MERGE_FIELD] === true;
exports.isDeepMergeField = isDeepMergeField;
/**
 * Из domain shape возвращает имена полей, помеченных `deepMerge()`.
 * Используется CRUD runtime для определения SQL merge-стратегии.
 */
const extractDeepMergeFields = (shape) => Object.entries(shape)
    .filter(([, s]) => (0, exports.isDeepMergeField)(s))
    .map(([k]) => k);
exports.extractDeepMergeFields = extractDeepMergeFields;
/**
 * Compose paradigm: domain (бизнес-shape) + platform (id/tenantId/timestamps)
 * → snapshot (то что хранится в БД и идёт через wire).
 *
 * Domain пишет ТОЛЬКО бизнес-поля, platform добавляется automatically через
 * `composeSnapshot(domain)`. Patch / create payload берут domain как есть —
 * `Omit` не нужен (platform-полей в domain нет, нечего исключать).
 *
 * **Reserved names** в namespace domain'ов — `id` / `tenantId` / `createdAt` /
 * `updatedAt`. Если domain объявит поле с одним из этих имён — `composeSnapshot`
 * перезатрёт platform-версией (zod `.merge()` semantics: правый аргумент wins).
 * Соглашение: domain эти имена не использует.
 *
 * @example
 *   const FooDomain = z.object({
 *     name: z.string(),
 *     status: z.enum(['active', 'paused']),
 *   });
 *
 *   const FooSnapshot = composeSnapshot(FooDomain);
 *   // = FooDomain.merge({id: EntityId, tenantId, createdAt, updatedAt})
 *   // z.infer → { name: string; status: ...; id: string; tenantId: string;
 *   //             createdAt: string; updatedAt: string }
 *
 *   const FooPatch = composePatch(FooDomain);
 *   // = FooDomain.partial() → { name?: string; status?: ... }
 *   // platform-полей в patch нет — невозможно случайно tenantId передать.
 *
 *   const FooCreatePayload = composeCreatePayload(FooDomain);
 *   // = FooDomain (identity, semantic alias для clarity в Routes)
 *
 * @see docs/crud-spec.md — полная спецификация compose paradigm
 */
/**
 * Platform-добавляемые поля каждого snapshot'а. Эти имена reserved by platform —
 * domain не должен их объявлять (см. JSDoc файла).
 *
 * - `id` — UUIDv7 (EntityId), генерится handler'ом команды create.
 * - `tenantId` — автоинжект из NATS envelope, immutable.
 * - `createdAt` / `updatedAt` — derived (server-managed), технические timestamps
 *   из БД (INSERT / UPDATE). Бизнес-семантические timestamps (например Session
 *   `openedAt`) — НЕ platform; domain добавляет их сам через `.extend({...})`
 *   поверх результата `composeSnapshot`.
 *
 * Reserved names: `id`, `tenantId`, `createdAt`, `updatedAt`.
 *
 * Расширение: если завтра добавим `softDeletedAt` — добавляется здесь, попадает
 * во все snapshot'ы автоматически. Единое место для эволюции platform-полей.
 */
exports.platformSnapshotFields = zod_1.z.object({
    id: id_1.EntityId,
    tenantId: zod_1.z.string().min(1),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
});
/**
 * Reserved-name collision guard: domain не должен объявлять поля с именами
 * platform (`.merge()` silently бы их перезатёр → debug-ад). Throw на module-load
 * (когда `composeSnapshot(domain)` вызывается top-level) — ошибка ловится сразу.
 */
const RESERVED_PLATFORM_NAMES = Object.keys(exports.platformSnapshotFields.shape);
const assertNoReservedNames = (domainKeys) => {
    const conflicts = domainKeys.filter((k) => RESERVED_PLATFORM_NAMES.includes(k));
    if (conflicts.length > 0) {
        throw new Error(`composeSnapshot: domain contains reserved platform names: [${conflicts.join(', ')}]. ` +
            `These names are added automatically (${RESERVED_PLATFORM_NAMES.join('/')}). ` +
            `Remove them from your Domain schema.`);
    }
};
const composeSnapshot = (domain) => {
    assertNoReservedNames(Object.keys(domain.shape));
    const shape = { ...domain.shape, ...exports.platformSnapshotFields.shape };
    return zod_1.z.object(shape);
};
exports.composeSnapshot = composeSnapshot;
/**
 * Composer: domain → patch (Partial). Semantic alias для `.partial()`.
 *
 * Patch payload используется в `cmd.<e>.update.payload.patch` —
 * `{id, patch: composePatch(FooDomain)}`. Поскольку domain не содержит platform-полей,
 * patch их тоже не содержит → невозможно случайно `patch.tenantId = 'other'`.
 *
 * @example
 *   const FooUpdateCommandV1 = CommandMessage.extend({
 *     type: z.literal('FooUpdateCommand'),
 *     version: z.literal(1),
 *     id: EntityId,
 *     patch: composePatch(FooDomain),
 *   });
 */
const composePatch = (domain) => zod_1.z.object((0, exports.extractClientShape)(domain.shape)).partial();
exports.composePatch = composePatch;
//# sourceMappingURL=compose.js.map